import { Fragment, useCallback, useEffect, useState } from 'react';

import { TablePagination } from '@mui/material';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { convertSellerRole } from 'utils/convertRole';
import { firstLetterUppercase, formatCPF, removeCpfFormatting } from 'utils/formatters';
import { pixKeyFormatter } from 'utils/pix-key-formatter';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  clearSellerSelected,
  setAllSellerList,
  setFilterCurrentPage,
  setFilterRowsPerPage,
  setSeller,
} from 'v3/data/store/seller/actions';
import { ISeller } from 'v3/data/store/seller/types';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { listAllSellers, updateActiveSeller } from 'v3/services/api';
import { SellerMobileTable } from 'v3/shared/components/MobileTable/SellerMobileTable';
import { SellerFormModal } from 'v3/shared/components/Modal/SellerFormModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SellerContactIcons } from 'v3/shared/components/SellerContactIcons';
import { SwitchCommon } from 'v3/shared/components/Switch';
import {
  CustomSubtitleMedium,
  EditOutlinedIconStyle,
  IconAlignmentAndSwitchCommon,
  InlineContainer,
  PixKeySubtitle,
  TableCellGroup,
  TableCellGroupSmall,
} from 'v3/shared/components/Table/SellerTables/styles';
import { PaperStyled } from 'v3/shared/components/Table/styles';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { theme } from 'v3/styles/global-styles';
import { TableCellStyled, TablePaginationStyles } from './styles';
import { sellerUpdateFactory } from './seller-update-factory';
import { sanitizeCpfOrName } from 'utils/sanitizeCpfOrName';

export const SellerTable = () => {
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const { isAdmin } = getStorePermissions();
  const [sellerBankAccountFeatureEnabled, setSellerBankAccountFeatureEnabled] = useState(false);
  const { sellersList, count } = useStoreSelector((store) => store.seller.sellerList);
  const initialSeller = useStoreSelector((store) => store.seller.seller);
  const { sellersFilter } = useStoreSelector((store) => store.seller);
  const { profileId } = useStoreSelector((store) => store.store.storeLogged);
  const { isDesktop } = useBreakpoints();
  const { page, rowsPerPage } = sellersFilter;

  const openEditSellerModal = (seller) => {
    setSeller(seller);
    setChangeModalOpen(true);
  };

  const fetchSellerList = useCallback(async () => {
    try {
      const response = await listAllSellers({
        rowsPerPage,
        page,
        searchString: sanitizeCpfOrName(sellersFilter.filter),
        active: sellersFilter.active,
        storeIds: sellersFilter.storeIds,
      });
      setAllSellerList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista dos consultores', error);
    }
  }, [sellersFilter, profileId]);

  const handleSwitchChange = async (seller: ISeller, event: React.ChangeEvent<HTMLInputElement>) => {
    const switchNewValue = Boolean(event.target.checked);

    try {
      await updateActiveSeller({
        sellerId: seller.id,
        active: switchNewValue,
      });

      switchNewValue
        ? showSnackbar('Ativado com sucesso', 'success', true)
        : showSnackbar('Desativado com sucesso', 'success', true);

      await fetchSellerList();
    } catch (error) {
      console.error(error);
    }
  };

  const sellerAlreadyCreated = async (cpf: string, storeId: string): Promise<boolean> => {
    try {
      const formattedCpf = removeCpfFormatting(cpf);
      await api.findSeller(formattedCpf, storeId);
      return true;
    } catch (error) {
      return false;
    }
  };


  const submitSellerUpdate = async (sellerData) => {
    const sellerId = sellerData.id;
    const normalizedCpf = removeCpfFormatting(sellerData.code);
    const isCpfChanged = normalizedCpf !== removeCpfFormatting(initialSeller.code);

    if (isCpfChanged) {
      const isDuplicate = await sellerAlreadyCreated(sellerData.code, sellerData.storeId);

      if (isDuplicate) {
        showSnackbar('Este CPF já está cadastrado para essa loja.', 'error', true);
        return;
      }
    }

    const payload = sellerUpdateFactory(sellerData, sellerBankAccountFeatureEnabled);


    try {
      await api.updateSeller(sellerId, payload);

      showSnackbar('Consultor(a) atualizado com sucesso', 'success', true);
      setChangeModalOpen(false);
      clearSellerSelected();
      fetchSellerList();
    } catch (error) {
      showSnackbar(
        'Algo deu errado. O CPF pode já estar cadastrado em outro grupo ou houve um erro ao processar a solicitação',
        'error',
        true,
      );
      console.error(error);
    }
  };

  useEffect(() => {
    isAdmin && sellersFilter.storeIds.length > 0 && fetchSellerList();
  }, [fetchSellerList, initialSeller, page, rowsPerPage]);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const featureBackofficeResponse = await api.getFlagStatus(
      FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_BACKOFFICE,
    );
    const featurePhysicalResponse = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_PHYSICAL);

    const isFeatureEnabled = featureBackofficeResponse.data.enabled || featurePhysicalResponse.data.enabled;

    setSellerBankAccountFeatureEnabled(isFeatureEnabled);
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  return (
    <Fragment>
      {sellersList.isEmpty ? (
        <NoItemsFound />
      ) : isDesktop ? (
        <PaperStyled>
          <TableContainer sx={{ maxHeight: 450 }}>
            <TableMui stickyHeader aria-label="sticky tables">
              <TableHead>
                <TableRow>
                  <TableCellStyled>Nome</TableCellStyled>
                  <TableCellStyled>Loja</TableCellStyled>
                  {sellerBankAccountFeatureEnabled && <TableCellStyled>Chave Pix</TableCellStyled>}
                  {sellerBankAccountFeatureEnabled && <TableCellStyled>Cargo</TableCellStyled>}
                  <TableCellStyled>Ações</TableCellStyled>
                </TableRow>
              </TableHead>

              <TableBody>
                {sellersList.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCellGroup>
                      <CustomSubtitleMedium>{firstLetterUppercase(seller.name)}</CustomSubtitleMedium>
                      <CustomSubtitleMedium color={theme.palette.secondary.main}>
                        {formatCPF(seller.code)}
                      </CustomSubtitleMedium>
                      {sellerBankAccountFeatureEnabled && (
                        <InlineContainer>
                          <SellerContactIcons
                            phoneAreaCode={seller.phoneAreaCode}
                            phoneNumber={seller.phoneNumber}
                            email={seller.email}
                          />
                        </InlineContainer>
                      )}
                    </TableCellGroup>

                    <TableCellGroup>
                      <CustomSubtitleMedium>{firstLetterUppercase(seller.store?.name)}</CustomSubtitleMedium>
                    </TableCellGroup>

                    {sellerBankAccountFeatureEnabled ? (
                      <Fragment>
                        <TableCellGroup>
                          <PixKeySubtitle>{seller.pixKey ? pixKeyFormatter(seller.pixKey) : '-'}</PixKeySubtitle>
                        </TableCellGroup>

                        <TableCellGroup>
                          <CustomSubtitleMedium>{convertSellerRole(seller.role)}</CustomSubtitleMedium>
                        </TableCellGroup>
                      </Fragment>
                    ) : null}

                    <TableCellGroupSmall>
                      <IconAlignmentAndSwitchCommon>
                        <CustomSubtitleMedium>Editar</CustomSubtitleMedium>
                        <EditOutlinedIconStyle onClick={() => openEditSellerModal(seller)} />
                        <SwitchCommon
                          checked={seller.active}
                          onChange={(event) => {
                            handleSwitchChange(seller, event);
                          }}
                        />
                      </IconAlignmentAndSwitchCommon>
                    </TableCellGroupSmall>
                  </TableRow>
                ))}
              </TableBody>
            </TableMui>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TablePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => {
              setFilterCurrentPage(value);
            }}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </PaperStyled>
      ) : (
        <SellerMobileTable
          sellerList={sellersList}
          getFunction={handleSwitchChange}
          updateSeller={openEditSellerModal}
        />
      )}
      <SellerFormModal
        modal={{
          active: changeModalOpen,
          title: 'Editar consultor(a)',
          activeButton: true,
          cancelButton: 'Cancelar',
          saveButton: 'Salvar alterações',
          initialState: initialSeller,
          type: 'edit',
        }}
        onClose={() => setChangeModalOpen(false)}
        onConfirm={submitSellerUpdate}
      />
    </Fragment>
  );
};
